import {
  Customer,
  CustomerAddress,
  CustomerSearchResponse,
  CustomerCreate,
  CustomerUpdate,
} from "../types/customers";

import apiClient from "./api";

class CustomersService {
  /**
   * Get all customers with optional search filter
   */
  async getCustomers(
    params: {
      skip?: number;
      limit?: number;
      search?: string;
    } = {},
  ): Promise<Customer[]> {
    return await apiClient.get<Customer[]>("/customers", { params });
  }

  /**
   * Search customers by phone, name, or email
   */
  async searchCustomers(
    params: {
      skip?: number;
      limit?: number;
      phone?: string;
      name?: string;
      email?: string;
    } = {},
  ): Promise<CustomerSearchResponse> {
    return await apiClient.get<CustomerSearchResponse>("/customers/search", {
      params,
    });
  }

  /**
   * Get customer by ID
   */
  async getCustomerById(id: number): Promise<Customer> {
    return await apiClient.get<Customer>(`/customers/${id}`);
  }

  /**
   * Create a new customer
   */
  async createCustomer(customerData: CustomerCreate): Promise<Customer> {
    return await apiClient.post<Customer>("/customers", customerData);
  }

  /**
   * Update an existing customer
   */
  async updateCustomer(
    id: number,
    customerData: CustomerUpdate,
  ): Promise<Customer> {
    return await apiClient.put<Customer>(`/customers/${id}`, customerData);
  }

  /**
   * Get customer addresses
   */
  async getCustomerAddresses(customerId: number): Promise<CustomerAddress[]> {
    return await apiClient.get<CustomerAddress[]>(
      `/customers/${customerId}/addresses`,
    );
  }

  /**
   * Create a new customer address
   */
  async createAddress(addressData: {
    customer_id: number;
    address_line1: string;
    address_line2?: string;
    city: string;
    state: string;
    zip_code: string;
    is_default?: boolean;
    delivery_instructions?: string;
    latitude?: number;
    longitude?: number;
  }): Promise<CustomerAddress> {
    return await apiClient.post<CustomerAddress>(
      "/customers/addresses",
      addressData,
    );
  }

  /**
   * Update an existing customer address
   */
  async updateAddress(
    id: number,
    addressData: CustomerAddress,
  ): Promise<CustomerAddress> {
    return await apiClient.put<CustomerAddress>(
      `/customers/addresses/${id}`,
      addressData,
    );
  }

  /**
   * Delete a customer address
   */
  async deleteAddress(id: number): Promise<void> {
    return await apiClient.delete<void>(`/customers/addresses/${id}`);
  }

  /**
   * Get customer order history
   */
  async getCustomerOrders(
    customerId: number,
    params: {
      skip?: number;
      limit?: number;
    } = {},
  ): Promise<any> {
    return await apiClient.get<any>(`/customers/${customerId}/orders`, {
      params,
    });
  }
}

// Create singleton instance
export const customersService = new CustomersService();
